"use client";

import React, { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { IoArrowBack, IoArrowForward } from "react-icons/io5";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import useModalScrollLock from "@/hooks/useModalScrollLock";
import GlobalForm from "@/components/forms/GlobalForm";
import {
  FinanceAndAccountingSubcategory,
  FormHeading,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";

interface TestimonialAccountingProps {
  pageName: string;
  title: React.ReactNode;
  testimonilData: any[];
  cardCount: string;
}

const TestimonialAccounting: React.FC<TestimonialAccountingProps> = ({
  pageName,
  title,
  testimonilData,
  cardCount,
}) => {
  const pathname = usePathname();
  const [isFormModelOpen, setIsFormModelOpen] = useState<boolean>(false);

  // Hide button on specific URLs
  const shouldHideButton =
    pathname === "/best-us-cpa-admission-online-course-fees-in-india" ||
    pathname === "/best-us-cma-coaching-in-india";

  const handleFormModelOpen = useCallback(() => {
    sessionStorage.setItem("modal-open", "true");
    setIsFormModelOpen(true);
  }, []);

  const handleFormModelClose = useCallback(() => {
    sessionStorage.removeItem("modal-open");
    setIsFormModelOpen(false);
  }, []);
  useModalScrollLock(isFormModelOpen, {
    restoreScrollPosition: true,
    preventTouchScroll: true,
  });

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "center", skipSnaps: false },
    [Autoplay({ delay: 5000, stopOnInteraction: true })],
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi],
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi],
  );
  const scrollTo = useCallback(
    (idx: number) => emblaApi && emblaApi.scrollTo(idx),
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <section className=" w-full relative">
      <Image
        src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/tesimonialBG.webp?tr=w-480,h-640,q-75"
        alt="Background graphic for Miles Education"
        fill
        className="object-center z-0"
        loading="lazy"
        quality={75}
        sizes="(max-width: 767px) 480px, 100vw"
      />
      <div className="main_container mx-auto py-6 md:py-10 relative z-10">
        <div className="flex flex-col items-center text-center">{title}</div>
        <div className="relative mt-8 md:mt-10 overflow-hidden">
          <button
            aria-label="Previous testimonial"
            onClick={scrollPrev}
            className="hidden md:flex absolute left-2 top-[20%]  h-10 w-10 items-center justify-center rounded-full bg-white shadow-md hover:shadow-lg border text-[#061A2F] z-10"
          >
            <IoArrowBack size={22} />
          </button>
          <button
            aria-label="Next testimonial"
            onClick={scrollNext}
            className="hidden md:flex absolute right-2 top-[20%] h-10 w-10 items-center justify-center rounded-full bg-white shadow-md hover:shadow-lg border text-[#061A2F] z-10"
          >
            <IoArrowForward size={22} />
          </button>

          <div className="" ref={emblaRef}>
            <div className="flex">
              {testimonilData?.map((testimonial, idx) => (
                <div className="min-w-0 flex-[0_0_100%] px-2 md:px-6" key={idx}>
                  <div className="mx-auto max-w-5xl">
                    <div className="relative rounded-2xl bg-white p-3 md:p-4 shadow-[0_10px_30px_rgba(6,26,47,0.08)] flex flex-col gap-3">
                      <p className="text-[#616161] text-sm md:text-base text-center max-w-3xl mx-auto leading-tight">
                        &quot;{testimonial.description.replace(/\.{3}$/, "...")}
                        &quot;
                      </p>
                      <div className="text-center text-[#222] font-normal text-sm md:text-base">
                        {testimonial.location}
                      </div>
                      <div className="flex justify-center">
                        <Image
                          src={`${testimonial.compLogo}`}
                          alt="company logo"
                          width={120}
                          height={40}
                          className="object-contain h-[40px]"
                          loading="lazy"
                          sizes="120px"
                        />
                      </div>
                      <div className="absolute left-1/2 -bottom-3 h-6 w-6 -translate-x-1/2 rotate-45 bg-white shadow-[0_10px_30px_rgba(6,26,47,0.08)]" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 relative">
            <div className="flex items-center justify-center  px-8">
              <div
                className="flex items-center gap-3 md:gap-4 transition-transform duration-500 ease-in-out"
                style={{
                  transform: `translateX(${
                    cardCount === "13"
                      ? (6 - selectedIndex) * 70
                      : cardCount === "7"
                        ? (3 - selectedIndex) * 70
                        : cardCount === "20"
                          ? (9.6 - selectedIndex) * 70
                          : cardCount === "5"
                            ? (2 - selectedIndex) * 70
                            : cardCount === "4"
                              ? (1.6 - selectedIndex) * 70
                              : cardCount === "3"
                                ? (1 - selectedIndex) * 70
                                : (4 - selectedIndex) * 70
                  }px)`,
                }}
              >
                {testimonilData?.map((testimonial, i) => (
                  <button
                    key={`img-${i}`}
                    onClick={() => scrollTo(i)}
                    className="focus:outline-none transition-all duration-300 ease-in-out transform hover:scale-105 flex-shrink-0"
                    aria-label={`Go to ${testimonial.name}'s testimonial`}
                  >
                    <div
                      className={`relative rounded-full overflow-hidden bg-white shadow-md transition-all duration-300 ${
                        i === selectedIndex
                          ? "h-20 w-20 ring-2 ring-[#FFBA08] ring-offset-1 scale-110"
                          : "w-14 h-14 ring-2 ring-gray-200 hover:ring-gray-300 opacity-70"
                      }`}
                    >
                      <Image
                        src={testimonial.personImg}
                        alt={testimonial.name}
                        width={80}
                        height={80}
                        className="h-full w-full object-cover"
                        loading="lazy"
                        sizes="80px"
                      />
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="text-[#061A2F] text-center font-semibold text-lg md:text-lg mt-4">
              {testimonilData?.[selectedIndex]?.name}
            </div>
          </div>
          {/* CTA - Hidden on /best-us-cpa-admission-online-course-fees-in-india */}
          {!shouldHideButton && (
            <div className="mt-8 text-center">
              <button
                onClick={handleFormModelOpen}
                className="block mx-auto bg-[#2A85FF] tracking-normal rounded-[12px_12px_12px_0px] text-white font-bold py-1 px-4 md:h-[32px] text-sm text-center md:mt-10 mt-5 md:w-[214px]"
              >
                Join the Community
              </button>
            </div>
          )}
        </div>
      </div>

      {isFormModelOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative h-[auto] mx-auto border border-white p-4 w-[400px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(51, 153, 255, 0.30) 100%)",
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
            }}
          >
            <button
              onClick={handleFormModelClose}
              className="text-white text-xl md:text-2xl absolute right-1 top-1 cursor-pointer"
            >
              ✕
            </button>
            <div>
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/starGroup.webp"
                alt="star group"
                width={34}
                height={38}
                className="absolute top-3 left-[-8px]"
                loading="lazy"
              />
              <GlobalForm
                course={pageName}
                courseId={pageName === "CPA" ? 1 : 2}
                formHeading={
                  FormHeading.SCHEDULE_A_VISIT_TO_MILES_OFFICE_OR_MEET_US_ONLINE
                }
                formType="page"
                isAStepperForm={true}
                pageType="accounting page"
                subCategory={
                  pageName === "CPA"
                    ? FinanceAndAccountingSubcategory.CPA
                    : FinanceAndAccountingSubcategory.CMA
                }
                submitButtonName="GET STARTED"
                successMessage={SuccessMessageType.ACCOUNTING}
                verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
                darkBackground={true}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
export default TestimonialAccounting;
